import { useState, useEffect } from "react";
import NGOBusinessInformation from "./Steps/NGOBusinessInformation";
import RegisteredTrustees from "./Steps/RegisteredTrustees";
import NGOBusinessDocuments from "./Steps/NGOBusinessDocuments";
import NGOCompleteOnboarding from "./Steps/NGOCompleteOnboarding";
import HeaderTitle from "../../Components/HeaderTitle";
import styles from "./NGO.module.scss";
import useDocumentsByCustomerSubType from "../../hooks/useDocumentsByCustomerSubType";
import { BusinessTypes } from "../../../Interfaces";

import { ICorporateCustomer } from "../../hooks/useCorporateCustomers";

export enum AllNGOBusinessSteps {
  BUSINESS_INFORMATION = 0,
  REGISTERED_TRUSTEE = 1,
  BUSINESS_DOCUMENTS = 2,
  COMPLETE_ONBOARDING = 3,
}

interface INGO {
  onComplete: () => void;
  onBack?: () => void;
  onBackHandlerReady?: (handler: () => void) => void;
  formData?: any;
  onStepChange?: (step: number) => void;
  navigateToStep?: number; // Allow parent to control which step to navigate to
  resumeCustomerData?: ICorporateCustomer;
}

const NGO: React.FC<INGO> = ({
  onComplete,
  onBack,
  onBackHandlerReady,
  formData = {},
  onStepChange,
  resumeCustomerData,
  navigateToStep,
}) => {
  const [currentStep, setCurrentStep] = useState(
    AllNGOBusinessSteps.BUSINESS_INFORMATION,
  );

  const { data: ngoDocuments, isLoading: isLoadingDocuments } =
    useDocumentsByCustomerSubType(BusinessTypes.NGO.toString());

  // Sync with parent stepper navigation (subtract 1 because parent stepper has "Get Started" as step 0)
  useEffect(() => {
    if (navigateToStep !== undefined && navigateToStep >= 1) {
      const internalStep = navigateToStep - 1;
      if (
        internalStep !== currentStep &&
        internalStep >= 0 &&
        internalStep <= AllNGOBusinessSteps.COMPLETE_ONBOARDING
      ) {
        setCurrentStep(internalStep);
      }
    }
  }, [navigateToStep]);

  const handleProceed = (_step: number) => {
    setCurrentStep(_step);
    // Notify parent about step change (add 1 because "Get Started" is step 0 in parent stepper)
    if (onStepChange) {
      onStepChange(_step + 1);
    }
  };

  // Notify parent when current step changes
  useEffect(() => {
    if (onStepChange) {
      onStepChange(currentStep + 1);
    }
  }, [currentStep, onStepChange]);

  const handleBackStep = () => {
    // Navigate backwards through steps
    switch (currentStep) {
      case AllNGOBusinessSteps.COMPLETE_ONBOARDING:
        setCurrentStep(AllNGOBusinessSteps.BUSINESS_DOCUMENTS);
        if (onStepChange) {
          onStepChange(AllNGOBusinessSteps.BUSINESS_DOCUMENTS + 1);
        }
        break;
      case AllNGOBusinessSteps.BUSINESS_DOCUMENTS:
        setCurrentStep(AllNGOBusinessSteps.REGISTERED_TRUSTEE);
        if (onStepChange) {
          onStepChange(AllNGOBusinessSteps.REGISTERED_TRUSTEE + 1);
        }
        break;
      case AllNGOBusinessSteps.REGISTERED_TRUSTEE:
        setCurrentStep(AllNGOBusinessSteps.BUSINESS_INFORMATION);
        if (onStepChange) {
          onStepChange(AllNGOBusinessSteps.BUSINESS_INFORMATION + 1);
        }
        break;
      case AllNGOBusinessSteps.BUSINESS_INFORMATION:
        // We're at the first step, go back to parent step
        if (onBack) {
          onBack();
        }
        break;
      default:
        break;
    }
  };

  // Expose the back handler to parent via callback
  useEffect(() => {
    if (onBackHandlerReady) {
      onBackHandlerReady(handleBackStep);
    }
  }, [currentStep, onBackHandlerReady, handleBackStep]);

  const getStepTitle = () => {
    switch (currentStep) {
      case AllNGOBusinessSteps.BUSINESS_INFORMATION:
        return "Business Information";
      case AllNGOBusinessSteps.REGISTERED_TRUSTEE:
        return "Registered Trustees";
      case AllNGOBusinessSteps.BUSINESS_DOCUMENTS:
        return "Business Documents";
      case AllNGOBusinessSteps.COMPLETE_ONBOARDING:
        return "Complete Onboarding";
      default:
        return "";
    }
  };

  const getStepSubtitle = () => {
    if (currentStep === AllNGOBusinessSteps.COMPLETE_ONBOARDING) {
      return "Review your onboarding progress and complete any outstanding steps.";
    }
    return "Kindly enter your information below";
  };

  return (
    <div className={styles.ngo}>
      <HeaderTitle>{getStepTitle()}</HeaderTitle>
      <p className={styles.subtitle}>{getStepSubtitle()}</p>

      <div className={styles.content}>
        {(() => {
          switch (currentStep) {
            case AllNGOBusinessSteps.BUSINESS_INFORMATION:
              return (
                <NGOBusinessInformation
                  handleProceed={handleProceed}
                  resumeCustomerData={resumeCustomerData!}
                  formData={formData}
                />
              );

            case AllNGOBusinessSteps.REGISTERED_TRUSTEE:
              return (
                <RegisteredTrustees
                  handleProceed={handleProceed}
                  resumeCustomerData={resumeCustomerData!}
                  formData={formData}
                />
              );

            case AllNGOBusinessSteps.BUSINESS_DOCUMENTS:
              return (
                <NGOBusinessDocuments
                  handleProceed={handleProceed}
                  resumeCustomerData={resumeCustomerData!}
                  formData={formData}
                  documentTypes={ngoDocuments?.data!}
                />
              );

            case AllNGOBusinessSteps.COMPLETE_ONBOARDING:
              return (
                <NGOCompleteOnboarding
                  setCurrentStep={setCurrentStep}
                  resumeCustomerData={resumeCustomerData!}
                  onComplete={onComplete}
                  formData={formData}
                />
              );

            default:
              return null;
          }
        })()}
      </div>
    </div>
  );
};

export default NGO;
